import { ArticleSource } from '@/schema';

import { request } from './client';

export type SavedSearch = {
  id: number;
  name: string;
  symbols: string[];
  sources: ArticleSource[];
  hasSymbolOnly: boolean;
};

export interface SavedSearchFiltersPayload {
  symbols: string[];
  sources: ArticleSource[];
  hasSymbolOnly?: boolean;
}

interface FetchSavedSearchesParams {
  signal?: AbortSignal;
}

interface SavedSearchListResponse {
  savedSearches?: ServerSavedSearch[] | null;
}

interface SavedSearchMutationResponse {
  savedSearch?: ServerSavedSearch | null;
}

export type ServerSavedSearch = {
  id: number;
  name: string;
  filters: Record<string, unknown> | null;
};

function isArticleSource(value: unknown): value is ArticleSource {
  return (
    typeof value === 'string' &&
    (Object.values(ArticleSource) as string[]).includes(value)
  );
}

function buildFiltersPayload(filters: SavedSearchFiltersPayload) {
  const payload: Record<string, unknown> = {
    symbols: filters.symbols,
    sources: filters.sources,
    hasSymbolOnly: Boolean(filters.hasSymbolOnly),
  };

  return payload;
}

function parseSavedSearch(
  serverSavedSearch: ServerSavedSearch
): SavedSearch | null {
  const rawFilters = serverSavedSearch.filters;
  const filters =
    rawFilters && typeof rawFilters === 'object' && !Array.isArray(rawFilters)
      ? (rawFilters as Record<string, unknown>)
      : {};

  const filtersObject = filters as {
    deleted?: unknown;
    symbols?: unknown;
    sources?: unknown;
    hasSymbolOnly?: unknown;
  };

  if (filtersObject.deleted === true) {
    return null;
  }

  const rawSymbols = filtersObject.symbols;
  const rawSources = filtersObject.sources;

  const symbols = Array.isArray(rawSymbols)
    ? rawSymbols.filter(
        (symbol): symbol is string => typeof symbol === 'string'
      )
    : [];

  const sources = Array.isArray(rawSources)
    ? rawSources.filter((source): source is ArticleSource =>
        isArticleSource(source)
      )
    : [];

  const hasSymbolOnly =
    typeof filtersObject.hasSymbolOnly === 'boolean'
      ? filtersObject.hasSymbolOnly
      : false;

  return {
    id: serverSavedSearch.id,
    name: serverSavedSearch.name,
    symbols,
    sources,
    hasSymbolOnly,
  };
}

export async function fetchSavedSearches({
  signal,
}: FetchSavedSearchesParams = {}): Promise<SavedSearch[]> {
  const payload = await request<SavedSearchListResponse>({
    path: '/api/saved-searches',
    credentials: 'include',
    signal,
  });

  const savedSearches = Array.isArray(payload?.savedSearches)
    ? payload.savedSearches
        .map((raw) => parseSavedSearch(raw))
        .filter((search): search is SavedSearch => Boolean(search))
    : [];

  return savedSearches;
}

export interface CreateSavedSearchPayload {
  name: string;
  filters: SavedSearchFiltersPayload;
}

export async function createSavedSearch(
  payload: CreateSavedSearchPayload
): Promise<SavedSearch> {
  const response = await request<SavedSearchMutationResponse>({
    path: '/api/saved-searches',
    method: 'POST',
    credentials: 'include',
    body: {
      name: payload.name,
      filters: buildFiltersPayload(payload.filters),
    },
  });

  const rawSavedSearch = response?.savedSearch ?? null;

  const mapped = rawSavedSearch ? parseSavedSearch(rawSavedSearch) : null;
  if (!mapped) {
    throw new Error('Saved search response missing data');
  }

  return mapped;
}

export interface UpdateSavedSearchPayload extends CreateSavedSearchPayload {
  id: number;
}

export async function updateSavedSearch(
  payload: UpdateSavedSearchPayload
): Promise<SavedSearch> {
  const response = await request<SavedSearchMutationResponse>({
    path: '/api/saved-searches',
    method: 'POST',
    credentials: 'include',
    body: {
      id: payload.id,
      name: payload.name,
      filters: buildFiltersPayload(payload.filters),
    },
  });

  const rawSavedSearch = response?.savedSearch ?? null;
  const mapped = rawSavedSearch ? parseSavedSearch(rawSavedSearch) : null;
  if (!mapped) {
    throw new Error('Saved search response missing data');
  }

  return mapped;
}

export async function deleteSavedSearch(id: number): Promise<void> {
  await request<unknown>({
    path: `/api/saved-searches/${id}`,
    method: 'DELETE',
    credentials: 'include',
  });
}
